import { graphql, Script } from "gatsby"
import { useState } from "react"
import { SEOHead } from "components/SEO"
import { usePageAssets } from "hooks/usePageAssets"
import Image from "components/ui/Image"

/**
 * Newsletter Module
 *
 * This is an embeddable content to be used in the Superset community page:
 * https://superset.apache.org/community/
 */
function Newsletter({ data }) {
  const [loaded, setLoaded] = useState(false)
  const { getAsset } = usePageAssets()
  const supersetThumb = getAsset("superset-thumb")

  const { allMarkdownRemark, site } = data

  const posts = allMarkdownRemark.nodes
    .map((node) => ({
      date: node.frontmatter.date,
      slug: node.fields?.slug,
      url: encodeURI(`${site.siteMetadata.siteUrl}/blog/${node.fields?.slug}`),
    }))
    .slice(0, 6)

  // Format date for display (MMMM YYYY format)
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
  }

  return (
    <>
      <Script
        src="https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js"
        onLoad={() => setLoaded(true)}
      />
      {loaded && (
        <Script>
          {`WebFont.load({
          google: {
            families: ['Roboto']
          }
        });`}
        </Script>
      )}
      {posts.length > 0 && (
        <div className="flex flex-col overflow-hidden rounded-1x border border-[#EDEDED] font-[Roboto] sm:flex-row">
          <Image
            className="max-h-[250px] w-full object-cover sm:w-56"
            src={supersetThumb}
          />
          <div className="px-5 py-5 md:px-10">
            <time className="text-base font-bold text-[#20A7C9]">
              {formatDate(posts[0].date)}
            </time>
            <h3 className="text-[28px] font-bold text-[#484848] md:text-[32px]">
              <a
                href={posts[0].url}
                className="transition-colors hover:text-[#20A7C9]"
                target="_blank"
                rel="noreferrer"
              >
                Apache Superset Community Newsletter
              </a>
            </h3>
            <a
              href={posts[0].url}
              className="relative z-[2] mt-3 inline-block rounded-1x bg-gradient-to-b from-[#20A7C9] to-[#0C8FAE] px-6 py-2 text-base font-bold text-white before:absolute before:left-0 before:top-0 before:z-[-1] before:block before:h-full before:w-full before:rounded-1x before:bg-gradient-to-b before:from-[#11b0d8] before:to-[#116f86] before:opacity-0 before:transition-opacity before:duration-300 before:content-[''] hover:text-white hover:before:opacity-100 md:mt-2"
              target="_blank"
              rel="noreferrer"
            >
              Read
            </a>
          </div>
        </div>
      )}
      {posts.length > 1 && (
        <div className="mt-5 grid grid-cols-2 gap-3 md:mt-8 md:grid-cols-5">
          {posts.slice(1, 6).map(({ date, url }, index) => (
            <a
              href={url}
              className="color-[#5F5F5F] block rounded-1x border border-[#C8DEE3] bg-[#F7FAFB] py-1 text-center text-base hover:text-[#20A7C9]"
              target="_blank"
              key={index}
              rel="noreferrer"
            >
              {formatDate(date)}
            </a>
          ))}
        </div>
      )}
    </>
  )
}

// Export a Head component for Gatsby Head API
export function Head() {
  return (
    <SEOHead
      title="Newsletter"
      meta={[
        {
          name: "robots",
          content: "noindex, nofollow",
        },
      ]}
    />
  )
}

export const pageQuery = graphql`
  {
    site {
      siteMetadata {
        siteUrl
      }
    }
    allMarkdownRemark(
      filter: {
        fields: { collection: { eq: "blog" } }
        frontmatter: { category: { eq: "NEWSLETTER" } }
      }
      sort: { frontmatter: { date: DESC } }
    ) {
      nodes {
        fields {
          slug
        }
        frontmatter {
          date
        }
      }
    }
  }
`

export default Newsletter
